"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { numberToMoney } from "@/util/moneyConversion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type OrderOutput,
  orderPaymentStatusTranslation,
  orderStatusTranslation,
  type TagsOutput,
} from "@/interfaces";
import { format, differenceInDays } from "date-fns";
import { Clipboard, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DelinquencyTableProps {
  orders: OrderOutput[];
}

export function DelinquencyTable({ orders }: DelinquencyTableProps) {
  const navigate = useNavigate();

  const handleNavigate = (route: string) => {
    navigate(route, { replace: true });
  };

  const getDelayClass = (date: Date | string | undefined) => {
    if (!date) return "";

    const days = differenceInDays(new Date(), new Date(date));

    if (days <= 30) return "text-blue-500";
    if (days <= 60) return "text-yellow-500";
    if (days <= 90) return "text-orange-500";
    return "text-red-500 font-bold";
  };

  if (!orders || orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground mb-2" />
        <h3 className="text-lg font-medium">
          Nenhum pedido inadimplente encontrado
        </h3>
        <p className="text-muted-foreground">
          Não há pedidos inadimplentes que correspondam aos critérios de filtro
          atuais.
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ID do Pedido</TableHead>
          <TableHead>Cliente</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Status de Pagamento</TableHead>
          <TableHead>Tags</TableHead>
          <TableHead className="text-right">Valor Total</TableHead>
          <TableHead className="text-right">Valor Pago</TableHead>
          <TableHead className="text-right">Valor Pendente</TableHead>
          <TableHead>Data do Pedido</TableHead>
          <TableHead>Dias em Atraso</TableHead>
          <TableHead>Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.map((order) => {
          const paidAmount =
            order.payments?.reduce(
              (total, payment) => total + payment.amount,
              0
            ) || 0;
          const pendingAmount = order.totalAmount - paidAmount;
          const daysLate = order.preparationDate
            ? differenceInDays(new Date(), new Date(order.preparationDate))
            : 0;

          return (
            <TableRow key={order.id}>
              <TableCell className="font-medium">{order.id}</TableCell>
              <TableCell>
                {typeof order.customer === "string"
                  ? order.customer
                  : order.customer?.name || "Cliente não identificado"}
              </TableCell>
              <TableCell>{orderStatusTranslation[order.status]}</TableCell>
              <TableCell>
                <Badge
                  variant={
                    order.paymentStatus === "PARTIALLY_PAID"
                      ? "outline"
                      : "secondary"
                  }
                >
                  {orderPaymentStatusTranslation[order.paymentStatus]}
                </Badge>
              </TableCell>
              <TableCell>
                {Array.isArray(order.tags) && order.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {order.tags.map((tag) => {
                      const tagObj = tag as TagsOutput;

                      return (
                        <Badge
                          key={tagObj.id}
                          style={{
                            backgroundColor:
                              tagObj.styling?.primaryColor ?? "#E0E0E0",
                            color: tagObj.styling?.fontColor ?? "#000000",
                            border: `1px solid ${
                              tagObj.styling?.secondaryColor ?? "#BDBDBD"
                            }`,
                          }}
                          className="rounded-sm px-1 py-0.5 text-xs"
                        >
                          {tagObj.name}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right">
                {numberToMoney(order.totalAmount)}
              </TableCell>
              <TableCell className="text-right">
                {numberToMoney(paidAmount)}
              </TableCell>
              <TableCell className="text-right font-medium text-destructive">
                {numberToMoney(pendingAmount)}
              </TableCell>
              <TableCell>
                {order.preparationDate
                  ? format(new Date(order.preparationDate), "dd/MM/yyyy")
                  : "N/A"}
              </TableCell>
              <TableCell className={getDelayClass(order.preparationDate)}>
                {daysLate} dias
              </TableCell>
              <TableCell>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => handleNavigate(`/pedidos/${order.id}`)}
                      >
                        <Clipboard className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Ver detalhes do pedido</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
